import clsx from 'clsx';
import { CubeIcon } from '@heroicons/react/24/outline';

import { Badge } from '@oysterjs/ui/badge';
import { Button } from '@oysterjs/ui/button';
import {
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle,
  MountableDialog
} from '@oysterjs/ui/dialog';

import { type CustomPresetFieldsFragment } from '~/api/graphql';
import { sharedAccessBadgeLabel } from '~/components/presets/presets.utils';
import { UseCaseChip } from './use-case-tile';
import { LINES_OF_BUSINESS, familyForPreset, type UseCaseSpec } from './use-case-catalog';

const InfoDialogShell = ({
  icon,
  title,
  summary,
  badges,
  examplesLabel,
  examples,
  onRun,
  onClose
}: {
  icon: React.ReactNode;
  title: string;
  summary: string;
  badges?: React.ReactNode;
  examplesLabel?: string;
  examples?: string[];
  onRun?: () => void;
  onClose: () => void;
}) => (
  <MountableDialog onClose={onClose} size="lg">
    <div className="flex items-center gap-3">
      <span className="shrink-0">{icon}</span>
      <DialogTitle className="m-0 text-wrap">{title}</DialogTitle>
    </div>
    <DialogDescription>{summary}</DialogDescription>
    {(badges || (examples && examples.length > 0)) && (
      <DialogBody>
        {examples && examples.length > 0 && (
          <>
            <p className="m-0 text-xs font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
              {examplesLabel ?? 'What people use it for'}
            </p>
            <ul className="m-0 mt-2.5 flex list-none flex-col gap-2 p-0">
              {examples.map((example) => (
                <li
                  key={example}
                  className="rounded-lg border border-neutral-200 dark:border-neutral-700 px-3 py-2.5 text-sm text-neutral-700 dark:text-neutral-300"
                >
                  {example}
                </li>
              ))}
            </ul>
          </>
        )}
        {badges && <div className="mt-4 flex flex-wrap items-center gap-1.5">{badges}</div>}
      </DialogBody>
    )}
    <DialogActions>
      <Button color="white" onClick={onClose}>
        Close
      </Button>
      {onRun && (
        <Button color="sky" onClick={onRun}>
          Start this preset
        </Button>
      )}
    </DialogActions>
  </MountableDialog>
);

export const UseCaseInfoDialog = ({
  useCase,
  onRun,
  onClose
}: {
  useCase: UseCaseSpec;
  onRun?: () => void;
  onClose: () => void;
}) => {
  const family = familyForPreset(useCase.preset);
  return (
    <InfoDialogShell
      icon={<UseCaseChip useCase={useCase} />}
      title={useCase.title}
      summary={useCase.about.summary}
      examples={useCase.about.examples}
      badges={
        <>
          {family && <Badge color="zinc">{family.title}</Badge>}
          {useCase.lines.map((line) => (
            <Badge key={line} color="cyan">
              {LINES_OF_BUSINESS[line].label}
            </Badge>
          ))}
        </>
      }
      onRun={onRun}
      onClose={onClose}
    />
  );
};

export const CustomPresetInfoDialog = ({
  preset,
  onRun,
  onClose
}: {
  preset: CustomPresetFieldsFragment;
  /** Omitted where there is nothing to run, e.g. an admin arranging someone else's home screen. */
  onRun?: () => void;
  onClose: () => void;
}) => {
  const network = sharedAccessBadgeLabel(preset);
  return (
    <InfoDialogShell
      icon={
        <span
          className={clsx(
            'inline-flex rounded p-2',
            'bg-purple-50 dark:bg-purple-500/10 text-purple-700 dark:text-purple-300'
          )}
        >
          <CubeIcon className="size-4" />
        </span>
      }
      title={preset.title}
      summary={preset.description || 'This preset has no description yet.'}
      badges={
        <>
          <Badge color="purple">Custom preset</Badge>
          {network && <Badge color="sky">{network}</Badge>}
          {preset.createdBy?.name && (
            <span className="text-xs text-neutral-500 dark:text-neutral-400">
              Built by {preset.createdBy.name}
            </span>
          )}
        </>
      }
      onRun={onRun}
      onClose={onClose}
    />
  );
};
